import React, { useState, useEffect, useContext } from "react";
import { Plus, Trash2, Save, RefreshCw } from "lucide-react";
import { FaEdit } from "react-icons/fa";
import { toast } from "react-hot-toast";
import { AuthContext } from "../context/AuthContext";
import {
  getCostingSettings,
  updatePrivateLabelMarginTiers,
} from "../api/costingSettingsApi";

const DEFAULT_TIERS = [
  { category: "Private Label: Retail Packaging", pricingMode: "Retail Packaging", volumeBasis: "Units", fromQty: 0, toQty: 4999, targetNetMarginPercent: 30.0, notes: "" },
  { category: "Private Label: Retail Packaging", pricingMode: "Retail Packaging", volumeBasis: "Units", fromQty: 5000, toQty: 9999, targetNetMarginPercent: 25.0, notes: "" },
  { category: "Private Label: Retail Packaging", pricingMode: "Retail Packaging", volumeBasis: "Units", fromQty: 10000, toQty: 24999, targetNetMarginPercent: 22.5, notes: "" },
  { category: "Private Label: Retail Packaging", pricingMode: "Retail Packaging", volumeBasis: "Units", fromQty: 25000, toQty: 100000, targetNetMarginPercent: 20.0, notes: "" },
  { category: "Private Label: Bulk", pricingMode: "Bulk", volumeBasis: "KG", fromQty: 0, toQty: 499, targetNetMarginPercent: 30.0, notes: "" },
  { category: "Private Label: Bulk", pricingMode: "Bulk", volumeBasis: "KG", fromQty: 500, toQty: 1199, targetNetMarginPercent: 27.5, notes: "" },
  { category: "Private Label: Bulk", pricingMode: "Bulk", volumeBasis: "KG", fromQty: 1200, toQty: 2399, targetNetMarginPercent: 25.0, notes: "" },
  { category: "Private Label: Bulk", pricingMode: "Bulk", volumeBasis: "KG", fromQty: 2400, toQty: 4999, targetNetMarginPercent: 22.5, notes: "" },
  { category: "Private Label: Bulk", pricingMode: "Bulk", volumeBasis: "KG", fromQty: 5000, toQty: 25000, targetNetMarginPercent: 20.0, notes: "" },
];

export default function PrivateLabelMarginTiersSection() {
  const { canWrite, user } = useContext(AuthContext);
  const canManage = canWrite("AccessCosting", "CostingSettings");

  const [tiers, setTiers] = useState([]);
  const [originalTiers, setOriginalTiers] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getCostingSettings();
      const loadedTiers = res.data?.privateLabelMarginTiers;
      if (Array.isArray(loadedTiers) && loadedTiers.length > 0) {
        setTiers(loadedTiers);
      } else {
        setTiers(DEFAULT_TIERS);
      }
    } catch (err) {
      console.error("Failed to load PL margin tiers:", err);
      toast.error("Failed to load Private Label margin tiers");
      setTiers(DEFAULT_TIERS);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = () => {
    setOriginalTiers(JSON.parse(JSON.stringify(tiers)));
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setTiers(originalTiers);
    setIsEditing(false);
  };

  const handleRowChange = (index, field, value) => {
    setTiers((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        if (field === "fromQty" || field === "toQty" || field === "targetNetMarginPercent") {
          return { ...row, [field]: value === "" ? "" : Math.max(0, Number(value) || 0) };
        }
        if (field === "category") {
          const isBulk = value === "Private Label: Bulk";
          return {
            ...row,
            category: value,
            pricingMode: isBulk ? "Bulk" : "Retail Packaging",
            volumeBasis: isBulk ? "KG" : "Units",
          };
        }
        return { ...row, [field]: value };
      })
    );
  };

  const handleAddRow = (category = "Private Label: Retail Packaging") => {
    const isBulk = category === "Private Label: Bulk";
    setTiers((prev) => [
      ...prev,
      {
        category,
        pricingMode: isBulk ? "Bulk" : "Retail Packaging",
        volumeBasis: isBulk ? "KG" : "Units",
        fromQty: 0,
        toQty: 0,
        targetNetMarginPercent: 20,
        notes: "",
        updatedBy: user?.email || user?.firstName || "Draft",
        updatedAt: new Date(),
      },
    ]);
  };

  const handleRemoveRow = (index) => {
    setTiers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const currentUserEmail = user?.email || (user?.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : "Admin");
      const cleanedTiers = tiers.map((t) => ({
        ...t,
        fromQty: Number(t.fromQty) || 0,
        toQty: Number(t.toQty) || 0,
        targetNetMarginPercent: Math.min(100, Math.max(0, Number(t.targetNetMarginPercent) || 0)),
        updatedBy: currentUserEmail,
      }));

      const res = await updatePrivateLabelMarginTiers(cleanedTiers);
      if (res?.success) {
        toast.success("Private Label margin tiers updated successfully");
        setTiers(res.data.privateLabelMarginTiers || cleanedTiers);
        setIsEditing(false);
      } else {
        toast.error("Failed to update Private Label margin tiers");
      }
    } catch (err) {
      console.error("Save PL Margin Tiers Error:", err);
      toast.error("Failed to save Private Label margin tiers");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8 flex items-center justify-center text-gray-500 gap-2">
        <RefreshCw className="w-5 h-5 animate-spin" /> Loading PL Margin Tiers...
      </div>
    );
  }

  const retailTiers = tiers.map((t, idx) => ({ ...t, originalIndex: idx })).filter((t) => t.category.includes("Retail"));
  const bulkTiers = tiers.map((t, idx) => ({ ...t, originalIndex: idx })).filter((t) => t.category.includes("Bulk"));

  const renderTable = (rows, categoryName, defaultCategory) => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold text-gray-900">{categoryName}</h3>
        {isEditing && (
          <button
            type="button"
            onClick={() => handleAddRow(defaultCategory)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add Tier
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
            <tr>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Mode</th>
              <th className="px-4 py-3">Volume Basis</th>
              <th className="px-4 py-3 text-right">From Qty</th>
              <th className="px-4 py-3 text-right">To Qty</th>
              <th className="px-4 py-3 text-right">Target Net Margin %</th>
              <th className="px-4 py-3">Notes</th>
              <th className="px-4 py-3">Last Modified</th>
              {isEditing && <th className="px-4 py-3 text-center">Action</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={isEditing ? 9 : 8} className="px-4 py-6 text-center text-gray-400 text-xs">
                  No volume margin tiers configured for this section.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const idx = row.originalIndex;
                return (
                  <tr key={`pl-tier-${idx}`} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-2.5 font-medium text-gray-900 whitespace-nowrap">{row.category}</td>
                    <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{row.pricingMode}</td>
                    <td className="px-4 py-2.5 text-gray-600 font-semibold whitespace-nowrap">{row.volumeBasis}</td>
                    <td className="px-4 py-2.5 text-right">
                      {isEditing ? (
                        <input
                          type="number"
                          min="0"
                          value={row.fromQty === "" ? "" : row.fromQty}
                          onChange={(e) => handleRowChange(idx, "fromQty", e.target.value)}
                          className="w-24 px-2.5 py-1 text-right border border-gray-300 rounded-md focus:ring-1 focus:ring-black outline-none font-mono"
                        />
                      ) : (
                        <span className="font-mono text-gray-800">{Number(row.fromQty).toLocaleString()}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {isEditing ? (
                        <input
                          type="number"
                          min="0"
                          value={row.toQty === "" ? "" : row.toQty}
                          onChange={(e) => handleRowChange(idx, "toQty", e.target.value)}
                          className="w-28 px-2.5 py-1 text-right border border-gray-300 rounded-md focus:ring-1 focus:ring-black outline-none font-mono"
                        />
                      ) : (
                        <span className="font-mono text-gray-800">{Number(row.toQty).toLocaleString()}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {isEditing ? (
                        <div className="flex items-center justify-end gap-1">
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            max="100"
                            value={row.targetNetMarginPercent === "" ? "" : row.targetNetMarginPercent}
                            onChange={(e) => handleRowChange(idx, "targetNetMarginPercent", e.target.value)}
                            className="w-20 px-2.5 py-1 text-right border border-gray-300 rounded-md focus:ring-1 focus:ring-black outline-none font-mono font-semibold text-emerald-700"
                          />
                          <span className="text-xs text-gray-500">%</span>
                        </div>
                      ) : (
                        <span className="font-mono font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                          {Number(row.targetNetMarginPercent).toFixed(1)}%
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {isEditing ? (
                        <input
                          type="text"
                          value={row.notes || ""}
                          onChange={(e) => handleRowChange(idx, "notes", e.target.value)}
                          placeholder="Optional notes..."
                          className="w-full px-2.5 py-1 border border-gray-300 rounded-md focus:ring-1 focus:ring-black outline-none text-xs"
                        />
                      ) : (
                        <span className="text-xs text-gray-500">{row.notes || "—"}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                      {row.updatedBy ? (
                        <div>
                          <div className="font-semibold text-gray-700">{row.updatedBy}</div>
                          <div className="text-[10px] text-gray-400">
                            {row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : ""}
                          </div>
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                    {isEditing && (
                      <td className="px-4 py-2.5 text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveRow(idx)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove tier"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-8 mb-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Private Label Margin Tiers</h2>
          <p className="text-sm text-gray-500 mt-1">
            Configure volume-based net target margin tiers for Private Label Retail and Bulk quoting.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isEditing ? (
            <>
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={saving}
                className="px-4 py-2 text-sm font-semibold border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !canManage}
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving..." : "Save Tiers"}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleStartEdit}
              disabled={!canManage}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-black text-white rounded-xl hover:bg-gray-800 transition-colors shadow-sm disabled:opacity-50"
            >
              <FaEdit /> Edit Tiers
            </button>
          )}
        </div>
      </div>

      {renderTable(retailTiers, "Retail Packaging Tiers (Units)", "Private Label: Retail Packaging")}
      {renderTable(bulkTiers, "Bulk Tiers (KG)", "Private Label: Bulk")}
    </div>
  );
}
